<template>
  <div
    v-if="hasTimer"
    class="subtask-timer d-flex align-items-center"
  >
    <div
      class="subtask-timer-display d-inline-flex align-items-center px-2 py-1 rounded mr-1"
      :class="subtask.timer_is_running ? 'subtask-timer-running' : 'subtask-timer-idle'"
    >
      <feather-icon
        :icon="subtask.timer_is_running ? 'ClockIcon' : 'WatchIcon'"
        size="14"
        class="mr-1"
      />
      <span class="subtask-timer-value">{{ displayTime }}</span>
      <span
        v-if="subtask.timer_is_running"
        class="subtask-timer-pulse ml-1"
      />
    </div>
    <div
      v-if="!readOnly && isOpen"
      class="subtask-timer-actions d-inline-flex align-items-center"
    >
      <template v-if="!subtask.timer_is_running">
        <div>
          <b-button
            variant="info"
            class="subtask-timer-btn mr-1"
            pill
            :disabled="starting"
            @click="startTimer"
          >
            <feather-icon
              icon="PlayIcon"
              size="14"
            />
            <span class="ml-1">{{ $t('subtasks.timer.start') }}</span>
          </b-button>
        </div>
      </template>
      <template v-else>
        <div class="d-block d-sm-inline-flex align-items-center">
          <b-button
            variant="warning"
            pill
            class="subtask-timer-btn mr-1"
            @click="stopTimer"
          >
            <feather-icon
              icon="PauseIcon"
              size="14"
            />
            <span class="ml-1">{{ $t('subtasks.timer.pause') }}</span>
          </b-button>
          <b-button
            variant="success"
            class="subtask-timer-btn mr-1"
            pill
            @click="openCompleteModal"
          >
            <feather-icon
              icon="CheckIcon"
              size="14"
            />
            <span class="ml-1">{{ $t('subtasks.timer.complete') }}</span>
          </b-button>
        </div>
      </template>
    </div>

    <b-modal
      v-model="completeModalVisible"
      :title="$t('subtasks.timer.completeTitle')"
      size="md"
      centered
      no-close-on-backdrop
      no-close-on-esc
      @hidden="onCompleteModalHidden"
    >
      <div>
        <b-form-group
          :label="$t('subtasks.timer.closingNote')"
          label-for="completeNote"
        >
          <template #label>
            {{ $t('subtasks.timer.closingNote') }} <span class="text-danger">*</span>
          </template>
          <b-form-textarea
            id="completeNote"
            v-model="completeNote"
            :placeholder="$t('subtasks.timer.notePlaceholder')"
            rows="4"
            :state="completeNoteError ? false : null"
          />
          <b-form-invalid-feedback :state="!completeNoteError">
            {{ completeNoteError }}
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
      <template #modal-footer>
        <div class="d-flex align-items-center justify-content-between w-100">
          <b-button
            variant="outline-danger"
            :disabled="completeSaving"
            @click="completeModalVisible = false"
          >
            {{ $t('common.cancel') }}
          </b-button>
          <b-button
            variant="success"
            :disabled="completeSaving"
            @click="submitComplete"
          >
            <b-spinner
              v-if="completeSaving"
              small
              class="mr-1"
            />
            {{ $t('subtasks.timer.complete') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal
      v-model="conflictModalVisible"
      :title="$t('subtasks.timer.conflictTitle')"
      size="md"
      centered
      hide-footer
      :no-close-on-backdrop="conflictSwitching"
      :no-close-on-esc="conflictSwitching"
      :hide-header-close="conflictSwitching"
    >
      <i18n
        path="subtasks.timer.conflictMessage"
        tag="p"
        class="mb-1"
      >
        <template #name>
          <strong v-text="activeTimerName" />
        </template>
      </i18n>
      <div
        v-if="activeTimer"
        class="mb-1"
      >
        <div
          v-if="activeTimer.task"
          class="text-muted small"
        >
          {{ $t('subtasks.timer.conflictTask') }} <span v-text="activeTimer.task" />
        </div>
        <div class="text-muted small">
          {{ $t('subtasks.timer.conflictStarted') }} <span v-text="activeTimerStartedText()" />
        </div>
      </div>
      <div class="d-flex justify-content-between align-items-center mt-1">
        <b-button
          variant="outline-secondary"
          :disabled="conflictSwitching"
          @click="conflictModalVisible = false"
        >
          {{ $t('common.cancel') }}
        </b-button>
        <b-button
          variant="warning"
          :disabled="conflictSwitching"
          @click="confirmSwitch"
        >
          <b-spinner
            v-if="conflictSwitching"
            small
            class="mr-1"
          />
          {{ $t('subtasks.timer.switch') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton, BModal, BFormTextarea, BFormInvalidFeedback, BSpinner, BFormGroup,
} from 'bootstrap-vue'
import { packageLimit } from '@/libs/packageLimits'
import { statusSlug } from '@/libs/definitions'

export default {
  name: 'SubtaskTimer',
  components: {
    BButton, BModal, BFormTextarea, BFormInvalidFeedback, BSpinner, BFormGroup,
  },
  props: {
    subtask: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    callback: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      tick: 0,
      liveInterval: null,
      heartbeatInterval: null,
      visibilityHandler: null,
      completeModalVisible: false,
      completeNote: '',
      completeNoteError: null,
      completeSaving: false,
      conflictModalVisible: false,
      activeTimer: null,
      conflictSwitching: false,
      starting: false,
    }
  },
  computed: {
    isOpen() {
      return statusSlug('subtask', this.subtask) === 'open'
    },
    hasTimer() {
      return packageLimit('PROJECTS', 1)
    },
    displayTime() {
      const total = this.totalSeconds
      const h = Math.floor(total / 3600)
      const m = Math.floor((total % 3600) / 60)
      const sec = total % 60
      return [h, m, sec].map(v => String(v).padStart(2, '0')).join(':')
    },
    totalSeconds() {
      // tick: her saniye güncellenir, canlı sayacın reaktif tetikleyicisi
      // eslint-disable-next-line no-void
      void this.tick
      const base = this.subtask.timer_duration || 0
      if (!this.subtask.timer_is_running) return base
      const started = this.subtask.timer_started_at ? this.moment(this.subtask.timer_started_at) : null
      const extra = started ? Math.max(0, Math.floor(this.moment().diff(started, 'seconds'))) : 0
      return base + extra
    },
    activeTimerName() {
      return (this.activeTimer && this.activeTimer.subtask) || this.$t('subtasks.timer.activeTimer')
    },
  },
  watch: {
    'subtask.timer_is_running': {
      handler(val) {
        if (val) {
          this.startLiveCounter()
          this.startHeartbeat()
        } else {
          this.stopLiveCounter()
          this.stopHeartbeat()
        }
      },
      immediate: true,
    },
  },
  created() {
    this.visibilityHandler = () => {
      if (document.hidden) {
        this.stopHeartbeat()
      } else if (this.subtask.timer_is_running) {
        this.$store.dispatch('subtasks/timerHeartbeat', this.subtask.id)
        this.startHeartbeat()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
  },
  beforeDestroy() {
    this.stopLiveCounter()
    this.stopHeartbeat()
    document.removeEventListener('visibilitychange', this.visibilityHandler)
  },
  methods: {
    startHeartbeat() {
      this.stopHeartbeat()
      this.heartbeatInterval = setInterval(() => {
        this.$store.dispatch('subtasks/timerHeartbeat', this.subtask.id)
      }, 5 * 60 * 1000)
    },
    stopHeartbeat() {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval)
        this.heartbeatInterval = null
      }
    },
    startLiveCounter() {
      this.stopLiveCounter()
      this.liveInterval = setInterval(() => {
        this.tick += 1
      }, 1000)
    },
    stopLiveCounter() {
      if (this.liveInterval) {
        clearInterval(this.liveInterval)
        this.liveInterval = null
      }
    },
    // BE'nin {status:false, message} metni axios'un genel hata mesajından önce gelir.
    async showTimerError(e, fallback) {
      this.$toast({
        component: (await import('@core/components/toastification/ToastificationContent.vue')).default,
        props: {
          variant: 'danger', title: this.$t('common.toast.errorShort'), icon: 'XIcon', text: e?.response?.data?.message || e?.message || fallback,
        },
      })
    },
    async startTimer() {
      if (this.starting) return
      this.starting = true
      try {
        const res = await this.$store.dispatch('subtasks/timerStart', { id: this.subtask.id })
        // Tek aktif timer: başka alt görevde açık timer varsa onay modalı göster.
        if (res && res.conflict) {
          this.activeTimer = res.active_timer || null
          this.conflictModalVisible = true
          return
        }
        this.callback()
      } catch (e) {
        await this.showTimerError(e, this.$t('subtasks.timer.startFailed'))
      } finally {
        this.starting = false
      }
    },
    activeTimerStartedText() {
      const startedAt = this.activeTimer && this.activeTimer.started_at
      return startedAt ? this.moment(startedAt).format('DD.MM.YYYY HH:mm') : '-'
    },
    async confirmSwitch() {
      if (this.conflictSwitching) return
      this.conflictSwitching = true
      try {
        await this.$store.dispatch('subtasks/timerStart', { id: this.subtask.id, force: true })
        this.conflictModalVisible = false
        // Liste yenilenince durdurulan alt görevin görünümü de güncellenir.
        this.callback()
      } catch (e) {
        // BE işlemi geri alır, eski timer çalışmaya devam eder; modal açık kalır.
        await this.showTimerError(e, this.$t('subtasks.timer.startFailed'))
      } finally {
        this.conflictSwitching = false
      }
    },
    async stopTimer() {
      try {
        await this.$store.dispatch('subtasks/timerStop', this.subtask.id)
        this.callback()
      } catch (e) {
        await this.showTimerError(e, this.$t('subtasks.timer.stopFailed'))
      }
    },
    openCompleteModal() {
      this.completeModalVisible = true
    },
    onCompleteModalHidden() {
      this.completeNote = ''
      this.completeNoteError = null
      this.completeSaving = false
    },
    async submitComplete() {
      this.completeNoteError = null
      if (!this.completeNote.trim()) {
        this.completeNoteError = this.$t('subtasks.timer.noteRequired')
        return
      }
      this.completeSaving = true
      try {
        if (this.subtask.timer_is_running) {
          await this.$store.dispatch('subtasks/timerStop', this.subtask.id).catch(() => {})
        }
        const res = await this.$store.dispatch('subtasks/completeSubtask', {
          id: this.subtask.id,
          result_notes: this.completeNote.trim(),
        })
        if (res?.status === false) {
          this.completeNoteError = res?.errors?.result_notes || res?.message || this.$t('subtasks.failed')
          return
        }
        this.completeModalVisible = false
        this.callback()
        this.$toast({
          component: (await import('@core/components/toastification/ToastificationContent.vue')).default,
          props: {
            variant: 'success', title: this.$t('common.toast.successShort'), icon: 'CheckIcon', text: this.$t('subtasks.timer.completed'),
          },
        })
      } catch (e) {
        this.$toast({
          component: (await import('@core/components/toastification/ToastificationContent.vue')).default,
          props: {
            variant: 'danger', title: this.$t('common.toast.errorShort'), icon: 'XIcon', text: e?.message || this.$t('subtasks.failed'),
          },
        })
      } finally {
        this.completeSaving = false
      }
    },
  },
}
</script>

<style scoped>
.subtask-timer-display {
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Consolas', monospace;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 10px 20px !important;
}
.subtask-timer-idle {
  background-color: rgba(0, 0, 0, 0.04);
  color: #6c757d;
}
.subtask-timer-running {
  background-color: rgba(0, 123, 255, 0.1);
  color: #0056b3;
}
.subtask-timer-value {
  letter-spacing: 0.02em;
  min-width: 4.2em;
}
.subtask-timer-pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #17a2b8;
  animation: subtask-timer-pulse 1.5s ease-in-out infinite;
}
@keyframes subtask-timer-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
.subtask-timer-btn {
  display: inline-flex;
  align-items: center;
}
.dark-layout .subtask-timer-idle {
  background-color: rgba(255, 255, 255, 0.06);
  color: #b4b7bd;
}
.dark-layout .subtask-timer-running {
  background-color: rgba(143, 155, 245, 0.16);
  color: #8f9bf5;
}
</style>
